import React, { useState, useEffect, Component } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { commentaryService } from '../../../services/bibleService';
import { SDACommentaryPanelSkeleton } from '../../../components/ui/SkeletonLoader';

// ── Error Boundary ────────────────────────────────────────────────────────────
class SDAErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('SDACommentaryDisplay error boundary caught:', error, info);
  }
  render() {
    if (this.state?.hasError) {
      return (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-600 mb-2">
            <Icon name="AlertCircle" size={15} />
            <span className="text-sm font-medium">SDA Commentary panel encountered an error</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => this.setState({ hasError: false })}
            className="text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
          >
            <Icon name="RefreshCw" size={13} className="mr-1" />
            Retry
          </Button>
        </div>
      );
    }
    return this.props?.children;
  }
}

// ── Abbreviation map for inline cross-reference expansion ────────────────────
const INLINE_ABBREV_MAP = {
  'Mt': 'Matthew', 'Matt': 'Matthew',
  'Mr': 'Mark', 'Mk': 'Mark', 'Mar': 'Mark',
  'Lu': 'Luke', 'Lk': 'Luke',
  'Jno': 'John', 'Jn': 'John',
  'Ac': 'Acts', 'Act': 'Acts',
  'Ro': 'Romans', 'Rom': 'Romans',
  'Ga': 'Galatians', 'Gal': 'Galatians',
  'Eph': 'Ephesians',
  'Php': 'Philippians', 'Phil': 'Philippians',
  'Col': 'Colossians',
  'He': 'Hebrews', 'Heb': 'Hebrews',
  'Jas': 'James',
  'Re': 'Revelation', 'Rev': 'Revelation',
  'Ps': 'Psalms', 'Psa': 'Psalms',
  'Pr': 'Proverbs', 'Pro': 'Proverbs', 'Prov': 'Proverbs',
  'Isa': 'Isaiah', 'Is': 'Isaiah',
  'Jer': 'Jeremiah', 'Je': 'Jeremiah',
  'Ge': 'Genesis', 'Gen': 'Genesis',
  'Ex': 'Exodus', 'Exo': 'Exodus',
  'De': 'Deuteronomy', 'Deu': 'Deuteronomy', 'Deut': 'Deuteronomy',
};

/**
 * Expand abbreviated book names in inline references like "Mt 5:3" → "Matthew 5:3"
 */
const expandInlineAbbrev = (text) => {
  if (!text) return text;
  // Match patterns like "Mt 5:3", "Jno 3:16", "Mr 1:1" at word boundaries
  return text?.replace(
    /\b((?:[123]\s)?(?:[A-Z][a-z]*|[A-Z]+))\s+(\d+:\d+(?:-\d+)?)\b/g,
    (match, bookPart, ref) => {
      const expanded = INLINE_ABBREV_MAP?.[bookPart];
      return expanded ? `${expanded} ${ref}` : match;
    }
  );
};

/**
 * Format a raw verse reference string (e.g. "1", "1-3", "5") into a
 * human-readable label like "Verse 1" or "Verses 1-3".
 */
const formatVerseReference = (ref) => {
  if (!ref) return ref;
  const trimmed = ref?.trim();
  // Range like "1-3"
  if (/^\d+-\d+$/?.test(trimmed)) {
    return `Verses ${trimmed}`;
  }
  // Single verse
  if (/^\d+$/?.test(trimmed)) {
    return `Verse ${trimmed}`;
  }
  // Already has a book name or other format — return as-is
  return trimmed;
};

/**
 * Strip the leading verse-number header that the SDA Commentary database
 * stores at the start of commentary_text.
 *
 * The raw text looks like:
 *   "1-3\n\n1-3 (Ps. 33:6, 9). A Reservoir of Means.—God spoke…"
 *
 * We want to remove:
 *   - An optional first line that is ONLY a verse reference (e.g. "1-3" or "5")
 *   - The repeated reference at the start of the body text (e.g. "1-3 " or "5 ")
 *
 * Result: "(Ps. 33:6, 9). A Reservoir of Means.—God spoke…"
 */
const stripVersePrefix = (text, verseRef) => {
  if (!text) return text;

  let cleaned = text;

  // 1. Remove a leading line that is ONLY the verse reference (digits/dash/space)
  //    followed by one or more blank lines.
  cleaned = cleaned?.replace(/^\d[\d\s\-]*\n[\n\r]*/m, '');

  // 2. Remove the verse reference repeated at the very start of the body text.
  //    Handles patterns like "1-3 ", "5 ", "1 " at the beginning of the remaining text.
  if (verseRef) {
    const escapedRef = verseRef?.trim()?.replace(/[-]/g, '\\-');
    const prefixRegex = new RegExp(`^${escapedRef}\\s+`);
    cleaned = cleaned?.replace(prefixRegex, '');
  }

  return cleaned?.trim();
};

const SDACommentaryDisplayInner = ({
  book,
  chapter,
  verse,
  verseNumber,
  verseId,
  isVisible,
  onToggleVisibility,
  onVerseReference,
  textSizePx,
  className = ''
}) => {
  const [commentaries, setCommentaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Resolve verse number from either prop
  const resolvedVerse = verse ?? verseNumber;

  // Load commentary when verse changes and component is visible
  useEffect(() => {
    if (isVisible && book && chapter && resolvedVerse) {
      loadCommentary();
    }
  }, [isVisible, book, chapter, resolvedVerse]);

  const loadCommentary = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await commentaryService?.getEllenWhiteComments(book, chapter, resolvedVerse);

      if (error) {
        setError('Failed to load commentary');
        console.error('Error loading SDA commentary:', error);
      } else {
        setCommentaries(data || []);
      }
    } catch (err) {
      setError('Failed to load commentary');
      console.error('Error loading SDA commentary:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  // Show skeleton while loading
  if (loading) {
    return <SDACommentaryPanelSkeleton />;
  }

  return (
    <div className={`bg-amber-500/5 border border-amber-500/20 rounded-lg ${className}`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Icon name="BookOpen" size={16} className="text-amber-500" />
            <h4 className="font-semibold text-foreground">SDA Bible Commentary</h4>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={onToggleVisibility}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-4">
            <div className="flex items-center justify-center space-x-2 text-red-600 mb-2">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm font-medium">{error}</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadCommentary}
              className="text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
            >
              <Icon name="RefreshCw" size={14} className="mr-1" />
              Retry
            </Button>
          </div>
        )}

        {/* No Commentary State */}
        {!loading && !error && commentaries?.length === 0 && (
          <div className="text-center py-4">
            <div className="flex items-center justify-center space-x-2 text-muted-foreground mb-2">
              <Icon name="FileText" size={16} />
              <span className="text-sm">No commentary available for this verse</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Commentary will be added when available
            </p>
          </div>
        )}

        {/* Commentary Content */}
        {!loading && !error && commentaries?.length > 0 && (
          <div className="space-y-4">
            {commentaries?.map((entry, index) => (
              <div key={entry?.id || index} className={index > 0 ? 'pt-4 border-t border-border' : ''}>
                {/* Verse Reference */}
                {entry?.verseReference && (
                  <p className="font-semibold text-amber-500 mb-2 uppercase tracking-wide" style={{ fontSize: textSizePx ? `calc(${textSizePx} - 2px)` : '11px' }}>
                    {formatVerseReference(entry?.verseReference)}
                  </p>
                )}
                {/* Commentary Text — strip duplicate verse prefix, then expand abbreviations */}
                <div className="prose prose-sm max-w-none">
                  <p className="text-foreground leading-relaxed whitespace-pre-line" style={{ fontSize: textSizePx || '15px', lineHeight: '1.6' }}>
                    {expandInlineAbbrev(stripVersePrefix(entry?.commentaryText, entry?.verseReference))}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SDACommentaryDisplay = (props) => (
  <SDAErrorBoundary>
    <SDACommentaryDisplayInner {...props} />
  </SDAErrorBoundary>
);

export default SDACommentaryDisplay;